import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const VendorManagementSystem = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <VendorAccounts />
    </div>
  );
};

const VendorAccounts = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');

  const [newVendor, setNewVendor] = useState({
    companyName: '',
    category: '',
    contactPerson: '',
    email: '',
    phone: '',
    status: 'Active',
    paymentTerms: 'Net 30',
    address: '',
    taxId: '',
    bankName: '',
    accountNumber: '',
    creditLimit: '',
    rating: 5,
    contractEndDate: '',
  });

  // Load initial data
  useEffect(() => {
    const mockCategories = [
      { id: 1, name: 'Food Supplies', description: 'Food ingredients suppliers', status: 'Active' },
      { id: 2, name: 'Beverage', description: 'Beverage suppliers', status: 'Active' },
      { id: 3, name: 'Housekeeping', description: 'Cleaning services', status: 'Active' },
      { id: 4, name: 'IT Services', description: 'Technology support', status: 'Active' },
      { id: 5, name: 'Maintenance', description: 'Equipment maintenance', status: 'Active' },
    ];

    const mockVendors = [
      {
        id: 1,
        vendorId: 'VEND-001',
        companyName: 'Fresh Foods Co.',
        category: 'Food Supplies',
        contactPerson: 'Rajesh Kumar',
        email: 'rajesh@freshfoods.com',
        phone: '+91 9876543210',
        status: 'Active',
        paymentTerms: 'Net 30',
        totalSpend: '₹12,50,000',
        lastOrder: '2024-01-15',
        address: 'Mumbai, India',
        taxId: 'GSTIN123456',
        bankName: 'State Bank of India',
        accountNumber: 'XXXXXX7890',
        creditLimit: '₹5,00,000',
        rating: 4,
        contractEndDate: '2024-12-31',
      },
      {
        id: 2,
        vendorId: 'VEND-002',
        companyName: 'Beverage Masters Ltd.',
        category: 'Beverage',
        contactPerson: 'Priya Sharma',
        email: 'priya@bevmasters.com',
        phone: '+91 9876543211',
        status: 'Active',
        paymentTerms: 'Net 45',
        totalSpend: '₹9,80,000',
        lastOrder: '2024-01-14',
        address: 'Delhi, India',
        taxId: 'GSTIN123457',
        bankName: 'HDFC Bank',
        accountNumber: 'XXXXXX7891',
        creditLimit: '₹3,00,000',
        rating: 5,
        contractEndDate: '2024-11-30',
      },
      {
        id: 3,
        vendorId: 'VEND-003',
        companyName: 'CleanPro Services',
        category: 'Housekeeping',
        contactPerson: 'Amit Patel',
        email: 'amit@cleanpro.com',
        phone: '+91 9876543212',
        status: 'Inactive',
        paymentTerms: 'Net 15',
        totalSpend: '₹7,50,000',
        lastOrder: '2024-01-10',
        address: 'Bangalore, India',
        taxId: 'GSTIN123458',
        bankName: 'ICICI Bank',
        accountNumber: 'XXXXXX7892',
        creditLimit: '₹2,00,000',
        rating: 3,
        contractEndDate: '2024-10-15',
      },
    ];

    setCategories(mockCategories);
    setVendors(mockVendors);
  }, []);

  const statusOptions = ['Active', 'Inactive', 'Pending'];
  const paymentTerms = ['Net 15', 'Net 30', 'Net 45', 'Net 60'];

  const filteredVendors = vendors.filter((vendor) => {
    const matchesSearch =
      vendor.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.vendorId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.contactPerson.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || vendor.status === statusFilter;
    const matchesCategory = categoryFilter === 'All' || vendor.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  const handleAddVendor = () => {
    const vendor = {
      id: vendors.length + 1,
      vendorId: `VEND-${String(vendors.length + 1).padStart(3, '0')}`,
      ...newVendor,
      totalSpend: '₹0',
      lastOrder: 'Never',
    };
    setVendors([...vendors, vendor]);
    setShowAddModal(false);
    resetNewVendor();
  };

  const handleEditVendor = () => {
    setVendors(vendors.map((v) => (v.id === selectedVendor.id ? selectedVendor : v)));
    setShowEditModal(false);
    setSelectedVendor(null);
  };

  const handleDeleteVendor = () => {
    setVendors(vendors.filter((v) => v.id !== selectedVendor.id));
    setShowDeleteModal(false);
    setSelectedVendor(null);
  };

  const resetNewVendor = () => {
    setNewVendor({
      companyName: '',
      category: '',
      contactPerson: '',
      email: '',
      phone: '',
      status: 'Active',
      paymentTerms: 'Net 30',
      address: '',
      taxId: '',
      bankName: '',
      accountNumber: '',
      creditLimit: '',
      rating: 5,
      contractEndDate: '',
    });
  };

  const openEditModal = (vendor) => {
    setSelectedVendor({ ...vendor });
    setShowEditModal(true);
  };

  const openDeleteModal = (vendor) => {
    setSelectedVendor(vendor);
    setShowDeleteModal(true);
  };

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const activeVendorsCount = vendors.filter((v) => v.status === 'Active').length;
  const totalSpend = vendors.reduce(
    (acc, vendor) => acc + parseInt(vendor.totalSpend.replace(/[^0-9]/g, '')) || 0,
    0
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Updated Header */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Vendor Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management')}
            className="btn text-gray-600 hover:text-black"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management/accounts')}
            className="btn text-black font-bold"
          >
            Vendor Accounts
          </button>
          {/* <button
            onClick={() => navigate("/dashboard/mdm/vendor-management/purchase-orders")}
            className="btn text-gray-600 hover:text-black"
          >
            Purchase Orders
          </button>
          <button
            onClick={() => navigate("/dashboard/mdm/vendor-management/payments")}
            className="btn text-gray-600 hover:text-black"
          >
            Payments
          </button> */}
        </div>
      </header>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Vendors</p>
              <p className="text-2xl font-bold text-gray-900">{vendors.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Vendors</p>
              <p className="text-2xl font-bold text-green-600">{activeVendorsCount}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Spend</p>
              <p className="text-2xl font-bold text-gray-900">₹{totalSpend.toLocaleString()}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <svg
                className="w-6 h-6 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters and Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[300px]">
              <input
                type="text"
                placeholder="Search by vendor name, ID, or contact person..."
                className="pl-10 pr-4 py-3 border border-gray-300 rounded-lg w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="absolute left-3 top-3.5 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </span>
            </div>

            <select
              className="border border-gray-300 rounded-lg px-4 py-3"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <select
              className="border border-gray-300 rounded-lg px-4 py-3"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="All">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <Link
              to="/dashboard/mdm/vendor-management/manage-categories"
              className="bg-white text-gray-700 border border-gray-300 px-4 py-3 rounded-lg hover:bg-gray-50 flex items-center space-x-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              <span>Manage Categories</span>
            </Link>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-black text-white px-4 py-3 rounded-lg hover:bg-gray-800 flex items-center space-x-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>Add Vendor</span>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Vendors Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">Vendor Details</th>
                <th className="text-left p-4 font-semibold text-gray-700">Contact Info</th>
                <th className="text-left p-4 font-semibold text-gray-700">Financials</th>
                <th className="text-left p-4 font-semibold text-gray-700">Status & Rating</th>
                <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map((vendor) => (
                <tr key={vendor.id} className="border-b hover:bg-gray-50 transition-colors">
                  <td className="p-4">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{vendor.companyName}</p>
                          <p className="text-sm text-gray-500">{vendor.vendorId}</p>
                        </div>
                      </div>
                      <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                        {vendor.category}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="font-medium text-gray-900">{vendor.contactPerson}</p>
                    <p className="text-sm text-gray-600">{vendor.email}</p>
                    <p className="text-sm text-gray-600">{vendor.phone}</p>
                  </td>
                  <td className="p-4">
                    <p className="font-semibold text-gray-900">{vendor.totalSpend}</p>
                    <p className="text-sm text-gray-600">Total Spend</p>
                    <p className="text-xs text-gray-500 mt-1">{vendor.paymentTerms}</p>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col space-y-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          vendor.status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : vendor.status === 'Inactive'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {vendor.status}
                      </span>
                      <div className="flex items-center space-x-1">
                        <span className="text-yellow-500 text-sm">
                          {getRatingStars(vendor.rating)}
                        </span>
                        <span className="text-xs text-gray-500">({vendor.rating})</span>
                      </div>
                      <p className="text-xs text-gray-500">Last: {vendor.lastOrder}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => openEditModal(vendor)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => openDeleteModal(vendor)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center space-x-1"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        <span>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredVendors.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="w-12 h-12 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
            >
              Add Your First Vendor
            </button>
          </div>
        )}
      </div>

      {/* Add Vendor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold">Add New Vendor</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.companyName}
                    onChange={(e) => setNewVendor({ ...newVendor, companyName: e.target.value })}
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.category}
                    onChange={(e) => setNewVendor({ ...newVendor, category: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.contactPerson}
                    onChange={(e) => setNewVendor({ ...newVendor, contactPerson: e.target.value })}
                    placeholder="Enter contact person name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.phone}
                    onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.status}
                    onChange={(e) => setNewVendor({ ...newVendor, status: e.target.value })}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Terms
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.paymentTerms}
                    onChange={(e) => setNewVendor({ ...newVendor, paymentTerms: e.target.value })}
                  >
                    {paymentTerms.map((term) => (
                      <option key={term} value={term}>
                        {term}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    rows="3"
                    value={newVendor.address}
                    onChange={(e) => setNewVendor({ ...newVendor, address: e.target.value })}
                    placeholder="Enter company address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.taxId}
                    onChange={(e) => setNewVendor({ ...newVendor, taxId: e.target.value })}
                    placeholder="Enter tax identification number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Credit Limit
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={newVendor.creditLimit}
                    onChange={(e) => setNewVendor({ ...newVendor, creditLimit: e.target.value })}
                    placeholder="Enter credit limit amount"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddVendor}
                disabled={
                  !newVendor.companyName ||
                  !newVendor.category ||
                  !newVendor.contactPerson ||
                  !newVendor.email
                }
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Add Vendor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vendor Modal */}
      {showEditModal && selectedVendor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold">Edit Vendor</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={selectedVendor.companyName}
                    onChange={(e) =>
                      setSelectedVendor({ ...selectedVendor, companyName: e.target.value })
                    }
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={selectedVendor.category}
                    onChange={(e) =>
                      setSelectedVendor({ ...selectedVendor, category: e.target.value })
                    }
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={selectedVendor.contactPerson}
                    onChange={(e) =>
                      setSelectedVendor({ ...selectedVendor, contactPerson: e.target.value })
                    }
                    placeholder="Enter contact person"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={selectedVendor.status}
                    onChange={(e) =>
                      setSelectedVendor({ ...selectedVendor, status: e.target.value })
                    }
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditVendor}
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedVendor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Delete</h3>
              <p className="text-gray-600">
                Are you sure you want to delete <strong>{selectedVendor.companyName}</strong>? This
                action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteVendor}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagementSystem;
